import { ISaSmsTenantDetail } from '@interfaces/sa-sms';
import { Button, Card, Empty, List, notification, Space, Tag } from 'antd';
import { useSaSmsWrite } from 'hooks/use-sa-sms-api';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SaSmsReasonModal } from './sa-sms-reason-modal';

type Props = { detail: ISaSmsTenantDetail; canEdit: boolean; onChanged: () => void };

/** Active abuse flags of the tenant, with "acknowledge" (keep recording, never re-pause) and "clear". */
export const SmsAbuseFlags: React.FC<Props> = ({ detail, canEdit, onChanged }) => {
    const { t } = useTranslation(['sa-sms']);
    const { send } = useSaSmsWrite();
    const [action, setAction] = useState<'acknowledgeFlags' | 'clearFlags' | null>(null);
    const flags = Object.entries(detail.config.config.flags ?? {});

    const confirm = async ({ reason }: { reason: string }) => {
        if (!action) return;
        await send('put', `/tenants/${detail.tenantId}/config`, { [action]: true, reason });
        notification.success({ message: t('saved') });
        setAction(null);
        onChanged();
    };

    return (
        <Card
            title={t('detail.flags')}
            extra={
                canEdit &&
                flags.length > 0 && (
                    <Space>
                        <Button onClick={() => setAction('acknowledgeFlags')}>
                            {t('flags.acknowledge')}
                        </Button>
                        <Button danger onClick={() => setAction('clearFlags')}>
                            {t('flags.clear')}
                        </Button>
                    </Space>
                )
            }
        >
            {flags.length === 0 ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('flags.none')} />
            ) : (
                <List
                    size="small"
                    dataSource={flags}
                    renderItem={([name, info]) => (
                        <List.Item>
                            <Tag color="warning">{t(`flags.${name}`, { defaultValue: name })}</Tag>
                            {info?.acknowledged && <Tag>{t('flags.acknowledged')}</Tag>}
                        </List.Item>
                    )}
                />
            )}
            <SaSmsReasonModal
                open={!!action}
                danger={action === 'clearFlags'}
                title={action ? t(`flags.confirm.${action}`) : ''}
                onCancel={() => setAction(null)}
                onConfirm={confirm}
            />
        </Card>
    );
};
